/* =====================================================================
   THEME RUNTIME (Jose 2026-09-07, Phase 2) — the live consumer of the
   owner-configured theme (Settings › Theme). Surfaces read the current
   ThemeConfig via useTheme() and paint with matteTone(); the Theme Studio
   pushes changes with setTheme() (instant, no reload) and index.tsx loads
   the saved per-shop theme on boot. Brand-color retint stays in
   branding.applyTheme(); this module owns the per-element TONES + intensity.
   BEHAVIOR-PIN: see memory project-slik-matte-barber-cards-palette-picker (Phase 2).
===================================================================== */
import { useSyncExternalStore } from 'react';
import type { CSSProperties } from 'react';
import { ThemeConfig, ThemeTone } from '../services/shopSettingsService';
import { shopSettingsService } from '../services/shopSettingsService';
import { applyTheme, SLIK_THEME } from './branding';

/** The tone mapping Jose chose 2026-09-07 — the default until a shop saves its own. */
export const DEFAULT_THEME: ThemeConfig = {
  brand: { primary: '#C0824A', hero: '#6f4a30', accent: '#2f4a7a' },
  tones: {
    cards: 'dark', heroes: 'light', buttonsPrimary: 'light', buttonsSecondary: 'medium', barberTiles: 'medium',
    portalHeroes: 'medium', portalButtons: 'light', portalCards: 'dark',
    publicHeroes: 'medium', publicButtons: 'light', publicBarberTiles: 'medium',
  },
  intensity: 0.6,
};

let current: ThemeConfig = DEFAULT_THEME;
const listeners = new Set<() => void>();

export function getTheme(): ThemeConfig { return current; }

export function setTheme(next: ThemeConfig): void {
  current = {
    ...DEFAULT_THEME, ...next,
    brand: { ...DEFAULT_THEME.brand, ...next.brand },
    tones: { ...DEFAULT_THEME.tones, ...next.tones },
  };
  listeners.forEach((l) => l());
}

function subscribe(fn: () => void): () => void { listeners.add(fn); return () => { listeners.delete(fn); }; }

/** React hook — re-renders the component whenever the theme changes. */
export function useTheme(): ThemeConfig { return useSyncExternalStore(subscribe, getTheme, getTheme); }

/** Tone for a named element key (falls back to medium). */
export function toneFor(key: string): ThemeTone { return current.tones[key] || 'medium'; }

// ---- matte gradient token (single source of truth for the light/medium/dark recipe) ----
const hexToRgb = (h: string): [number, number, number] => {
  let s = h.replace('#', ''); if (s.length === 3) s = s.split('').map((c) => c + c).join('');
  const n = parseInt(s, 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const toHex = (r: number, g: number, b: number) =>
  '#' + [r, g, b].map((x) => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, '0')).join('');
const dk = (h: string, a: number) => { const [r, g, b] = hexToRgb(h); return toHex(r * (1 - a), g * (1 - a), b * (1 - a)); };
const lt = (h: string, a: number) => { const [r, g, b] = hexToRgb(h); return toHex(r + (255 - r) * a, g + (255 - g) * a, b + (255 - b) * a); };

/** Matte depth shadow scaled by intensity (0..1). */
export function matteShadow(k: number = current.intensity): string {
  return `0 ${6 + 8 * k}px ${16 + 14 * k}px -12px rgba(0,0,0,${0.35 + 0.25 * k}), inset 0 1px 0 rgba(255,255,255,0.07)`;
}

/** The reusable matte surface style: gradient + shadow + border + legible text.
 *  tone/intensity default to the live theme; pass a fixed value to opt out. */
export function matteTone(
  hex: string,
  tone: ThemeTone = 'medium',
  intensity: number = current.intensity,
): CSSProperties {
  const span = 0.24 + 0.30 * intensity;
  const shadow = matteShadow(intensity);
  if (tone === 'dark')
    return { background: `linear-gradient(140deg, ${dk(hex, 0.08)} 0%, ${dk(hex, 0.08 + span)} 100%)`, color: '#fff', boxShadow: shadow, border: '1px solid rgba(255,255,255,0.10)' };
  if (tone === 'medium')
    return { background: `linear-gradient(140deg, ${lt(hex, 0.16)} 0%, ${dk(hex, span * 0.6)} 100%)`, color: '#fff', boxShadow: shadow, border: '1px solid rgba(255,255,255,0.10)' };
  return { background: `linear-gradient(140deg, ${lt(hex, 0.46)} 0%, ${lt(hex, 0.06)} 100%)`, color: '#2a1f16', boxShadow: shadow, border: '1px solid rgba(0,0,0,0.06)' };
}

/** Convenience: paint an element with the tone assigned to a theme element key. */
export function elementStyle(elementKey: string, hex: string): CSSProperties {
  return matteTone(hex, toneFor(elementKey), current.intensity);
}

/** Load the saved per-shop theme on boot: retint the brand + set the tone runtime.
 *  Non-fatal — keeps DEFAULT_THEME + SLIK brand on any error. Call once from index.tsx. */
export async function loadThemeFromSettings(): Promise<void> {
  try {
    const s = await shopSettingsService.getSettings();
    if (s.theme) {
      setTheme(s.theme);
      applyTheme({ ...SLIK_THEME, primary: s.theme.brand?.primary || SLIK_THEME.primary });
    }
  } catch { /* keep defaults */ }
}
